import { sanitize } from 'dompurify';
import {
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material';
import React from 'react';
import { CerebroDrawer, CerebroHeading, CerebroSecondaryButton, CerebroTextButton } from '../../../shared/components';
import { DataRoomEmail, DataRoomUser } from '../../../shared/models';
import { useDataRoomLenderEmailQuery } from '../../../shared/hooks';
import { Skeleton } from '@mui/lab';
import { DateUtils } from '../../../shared/utils';

interface ViewSentEmailsProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null | undefined;
	close: () => void;
}

export const ViewSentEmails: React.FC<ViewSentEmailsProps> = ({ dataRoomGuid, user, close }) => {
	const [viewEmail, setViewEmail] = React.useState<DataRoomEmail | null>(null);
	const emailQuery = useDataRoomLenderEmailQuery(dataRoomGuid, user?.guid, {
		enabled: user != null,
		retry: false,
	});

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>View Sent Emails</CerebroHeading>
			<CerebroHeading size="h2" component="h2">
				{user?.firstName} {user?.lastName} ({user?.bankName})
			</CerebroHeading>

			<TableContainer sx={{ marginTop: 2 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Subject</TableCell>
							<TableCell>Sent By</TableCell>
							<TableCell>Sent On</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{emailQuery.isLoading && (
							<>
								<Skeleton height={60} />
								<Skeleton height={60} />
								<Skeleton height={60} />
							</>
						)}
						{emailQuery.isSuccess && (
							<>
								{emailQuery.data.map(email => (
									<TableRow key={email.guid}>
										<TableCell>{email.subject}</TableCell>
										<TableCell>{email.sentBy}</TableCell>
										<TableCell>
											{DateUtils.FormatStringAsShortDate(email.sentOn?.toString())}
										</TableCell>
										<TableCell>
											<CerebroSecondaryButton onClick={() => setViewEmail(email)}>
												View
											</CerebroSecondaryButton>
										</TableCell>
									</TableRow>
								))}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={viewEmail !== null} onClose={close}>
				<DialogTitle>{viewEmail?.subject}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<div
							dangerouslySetInnerHTML={{
								__html: sanitize(formatAndReplaceEmailTags(user, viewEmail?.body ?? '<p></p>')),
							}}
						></div>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<CerebroTextButton onClick={() => setViewEmail(null)}>Close</CerebroTextButton>
				</DialogActions>
			</Dialog>
		</CerebroDrawer>
	);
};

function formatAndReplaceEmailTags(user: DataRoomUser | null | undefined, emailBody: string): string {
	let body = emailBody.replace('#bankerFirstName', user?.firstName ?? '');
	body = body.replace('#loginLink', '<a href="javascript:void(0);">click here</a>');
	body = body.replace('#dataRoomLink', '<a href="javascript:void(0);">click here</a>');

	return body;
}
